import { AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LessonProgress } from '@/types';

// Panel de confirmación de completación de una lección (con requisitos de lectura
// y de % de video). Extraído de lesson-row.tsx por ENG-LOC-01.
export function LessonConfirmPanel({
  lesson,
  isCompleting,
  requiresReadingConfirmation,
  requiresVideoWatch,
  videoMet,
  minVideoPercent,
  videoWatchPercent,
  readingConfirmed,
  onReadingConfirmedChange,
  hasScrolledContent,
  onConfirm,
  onClose,
}: {
  lesson: LessonProgress;
  isCompleting: boolean;
  requiresReadingConfirmation: boolean;
  requiresVideoWatch: boolean;
  videoMet: boolean;
  minVideoPercent?: number;
  videoWatchPercent: number;
  readingConfirmed: boolean;
  onReadingConfirmedChange: (checked: boolean) => void;
  hasScrolledContent: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const blocked =
    isCompleting ||
    (requiresReadingConfirmation && !readingConfirmed) ||
    (requiresVideoWatch && !videoMet);

  return (
    <li className="border-t border-border bg-amber-50/50 px-5 py-4 dark:bg-amber-950/10">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <p className="text-sm font-medium text-card-foreground">Confirmar completación</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground">
          ¿Está seguro de que desea marcar la lección{' '}
          <span className="font-medium text-card-foreground">&ldquo;{lesson.title}&rdquo;</span>{' '}
          como completada? Esta acción no se puede deshacer.
        </p>

        {/* Video watch requirement notice */}
        {requiresVideoWatch && !videoMet && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Debe ver al menos {minVideoPercent}% del video antes de completar. Progreso actual:{' '}
            {videoWatchPercent}%.
          </p>
        )}

        {/* Reading confirmation checkbox (documents, content, presentations) */}
        {requiresReadingConfirmation && (
          <div className="space-y-1.5">
            <label
              className={cn(
                'flex items-start gap-3 rounded-lg border bg-background p-3',
                hasScrolledContent
                  ? 'cursor-pointer border-border'
                  : 'cursor-not-allowed border-border/50 opacity-60',
              )}
            >
              <input
                type="checkbox"
                checked={readingConfirmed}
                disabled={!hasScrolledContent}
                onChange={(e) => onReadingConfirmedChange(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary accent-primary disabled:cursor-not-allowed"
              />
              <span className="text-sm text-muted-foreground">
                Confirmo que he revisado el contenido de esta lección y comprendo su contenido e
                implicaciones.
              </span>
            </label>
            {!hasScrolledContent && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Debe abrir y revisar el contenido completo antes de confirmar.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={blocked}
            className={cn(
              'rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90',
              blocked && 'cursor-not-allowed opacity-60',
            )}
          >
            {isCompleting ? 'Guardando...' : 'Sí, completar'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-xs font-medium text-card-foreground hover:bg-accent"
          >
            Cancelar
          </button>
        </div>
      </div>
    </li>
  );
}
